import { css } from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import {
  faFaceGrinWide, faFaceSunglasses, faLockAlt,
} from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons/button/button.types';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { PasswordStrengthIndicatorComponent } from '~/_shared/components/passwordStrengthIndicator/passwordStrengthIndicator.component';
import {
  Breakpoint, mq,
} from '~/_shared/style.mixins';
import { useTranslation } from '~/_shared/utils/hooks';
import type { FormField } from '~/authentication/authenticationFormField.types';
import { ITEMS_GAP } from '~/authentication/registerPage/form/registerForm.component';
import type { UserRegisterErrors } from '~/store/userData/repository/userData.repository';
import { authorizationPagesErrorStyle } from '../../authenticationPages.styles';
import {
  AuthorizationButtonComponent, AuthorizationSubmitButtonComponent,
} from '../../submitButton/authenticationSubmitButton.component';
import { RegisterFormFooter } from './registerFormFooter.component';

const rowWithColumnsStyle = css({
  [mq(Breakpoint.mid)]: {
    flexDirection: 'row',
    gap: 8,
  },
});

const columnStyle = css({
  flex: 1,
  minWidth: 0,
});

const passwordStrengthWrapperStyle = css({
  fontSize: 14,
  width: '80%',
  margin: '0 auto',
});

const sigUpButtonStyle = css({
  width: '85%',
  [mq(Breakpoint.mid)]: {
    width: 'unset',
  },
});

const alertStyle = css({
  marginTop: 10,
});

const backButtonWrapperStyle = css({
  marginTop: 24,
});

type RegisterFormEmailViewProps = Readonly<{
  email: FormField;
  firstName: FormField;
  lastName: FormField;
  password: FormField;
  apiError: Readonly<{ message?: string; errors?: UserRegisterErrors | null; onClose: () => void }>;
  isLoading: boolean;
  onBackToOptions: () => void;
}>;

export const RegisterFormEmailView: FC<RegisterFormEmailViewProps> = (props) => {
  const [t] = useTranslation();

  return (
    <Stack gap={ITEMS_GAP}>
      <Stack
        css={rowWithColumnsStyle}
        gap={ITEMS_GAP}
      >
        <div css={columnStyle}>
          <TextInputAuthComponent
            autoComplete="given-name"
            hasError={props.firstName.errors.length > 0}
            icon={faFaceGrinWide}
            isDisabled={props.isLoading}
            label={t('First Name')}
            name="given-name"
            onBlur={props.firstName.onBlur}
            onChange={props.firstName.onChange}
            placeholder={t('First Name')}
            value={props.firstName.value}
          />

          {props.firstName.errors.length > 0 && (
            <FormErrorMessageComponent
              css={authorizationPagesErrorStyle}
              isSingleLine={false}
              messages={props.firstName.errors}
            />
          )}
        </div>

        <div css={columnStyle}>
          <TextInputAuthComponent
            autoComplete="family-name"
            hasError={props.lastName.errors.length > 0}
            icon={faFaceSunglasses}
            isDisabled={props.isLoading}
            label={t('Last Name')}
            name="family-name"
            onBlur={props.lastName.onBlur}
            onChange={props.lastName.onChange}
            placeholder={t('Last Name')}
            value={props.lastName.value}
          />

          {props.lastName.errors.length > 0 && (
            <FormErrorMessageComponent
              css={authorizationPagesErrorStyle}
              isSingleLine={false}
              messages={props.lastName.errors}
            />
          )}
        </div>
      </Stack>

      <div css={columnStyle}>
        <TextInputAuthComponent
          hasError={props.email.errors.length > 0}
          icon={faEnvelope}
          isDisabled={props.isLoading}
          label={t('Confirm Email')}
          name="email"
          onBlur={props.email.onBlur}
          onChange={props.email.onChange}
          placeholder={t('Confirm Email')}
          value={props.email.value}
        />

        {props.email.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            isSingleLine={false}
            messages={props.email.errors}
          />
        )}
      </div>

      <Stack gap={8}>
        <div>
          <TextInputAuthComponent
            autoComplete="new-password"
            hasError={props.password.errors.length > 0}
            icon={faLockAlt}
            isDisabled={props.isLoading}
            label={t('Password')}
            name="password"
            onBlur={props.password.onBlur}
            onChange={props.password.onChange}
            placeholder={t('Password')}
            type="password"
            value={props.password.value}
          />

          {props.password.errors.length > 0 && (
            <FormErrorMessageComponent
              css={authorizationPagesErrorStyle}
              isSingleLine={false}
              messages={props.password.errors}
            />
          )}
        </div>

        <div css={passwordStrengthWrapperStyle}>
          <PasswordStrengthIndicatorComponent password={props.password.value} />
        </div>
      </Stack>

      <div css={sigUpButtonStyle}>
        <AuthorizationSubmitButtonComponent
          isLoading={props.isLoading}
        >
          {t('Create Account')}
        </AuthorizationSubmitButtonComponent>
      </div>

      {props.apiError.message && !props.apiError.errors && (
        <AlertComponent
          css={alertStyle}
          onClose={props.apiError.onClose}
          stylingOptions={{ withoutBorder: true }}
          type="dangerAlternative"
        >
          {props.apiError.message}
        </AlertComponent>
      )}

      <RegisterFormFooter />

      <div css={backButtonWrapperStyle}>
        <AuthorizationButtonComponent
          buttonStyle={ButtonStyle.Secondary}
          isDisabled={props.isLoading}
          isLoading={false}
          onClick={props.onBackToOptions}
          size={ButtonSize.Medium}
        >
          {t('signup.email.signUpAnotherWay')}
        </AuthorizationButtonComponent>
      </div>
    </Stack>
  );
};
